import PhotoActions from '../common/PhotoActions';
import usePersonImageModalHandlers from '../person-image-modal/usePersonImageModalHandlers';
import { useSelector } from 'react-redux';
import { selectActiveItems } from '../../redux/slices/clothingItemsSlice';
import BackgroundCanvas from '../background/BackgroundCanvas';
import PersonImageLayer from '../person-image-layer/PersonImageLayer';
import PersonPlaceholder from '../common/PersonPlaceholder';
import CanvasToolbar from '../canvas-toolbar/CanvasToolbar';

import GridOverlay from '../positioning-helpers/GridOverlay';
import SnapGuides from '../positioning-helpers/SnapGuides';
import TryOnLoadingIndicator from '../virtual-try-on/TryOnLoadingIndicator';
import TryOnErrorHandler from '../virtual-try-on/TryOnErrorHandler';
import SelectionFeedback from '../selection-feedback/SelectionFeedback';
import CanvasItem from '../canvas-item/CanvasItem';
import { usePersonImages } from '../../hooks/usePersonImages';
import usePersonImage from '../../hooks/usePersonImage';
import { useVTOResults } from '../../hooks/useVTOResults';
import { useCheckLicenseQuery } from '../../redux/apis/endpoints/licensingApi';
import { useMemo } from 'react';
import { resolveLicensedPreviewUrl } from '../../utils/watermark';

const keepCanvasOpen = () => {};

const MyClosetCanvas = ({
  // Canvas props
  placeholderRef,
  placeholderWidth,
  placeholderHeight,
  background,
  onDragOver,
  onDragLeave,
  onDrop,
  onPlaceholderClick,
  panelPosition = 'right',

  // Grid and snap guides
  showGrid,
  gridSize,
  showSnapGuides,
  isMoving,
  snapLines,

  // Virtual try-on state
  isVirtualMode,
  toggleMode,
  onModeChange,
  isTryOnLoading,
  tryOnError,
  clearTryOnError,

  // Dropped items
  droppedItems,
  selectedItem,
  multiSelectedItems,
  onItemClick,
  onMoveStart,
  onMoveEnd,
  onResizeStart,
  onResizeEnd,
  onRotateStart,
  onRotateEnd,
  onItemMove,
  onItemResize,
  onItemRotate,
  onRevertToOriginal,
  onRetryVirtualTryOn,

  // Modal handlers for canvas clicks
  onPersonImageModalOpen,
  onBackgroundModalOpen,
  onLoginRequired,

  // Toolbar — direct modal handlers (skip VTO collapse)
  onDirectPersonModalOpen,
  onDirectBackgroundModalOpen,

  // Toolbar panel toggles
  onClothingPanelToggle,
  onLookbookToggle,
  onCommunityToggle,
  onEarningsToggle,
  isClothingPanelActive,
  isLookbookActive,
  isCommunityActive,
  isEarningsActive,
  isPersonModalOpen,
  isBackgroundModalOpen,

  // Selection feedback
  selectionFeedback,

  // Person image upload loading
  isPersonImageUploading
}) => {
  const { handleLocalFileUpload, isPhotoUploading } = usePersonImageModalHandlers({ onClose: keepCanvasOpen });
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  // Get person image state from Redux using RTK Query
  const {
    activePersonImage,
    position,
    scale,
    isVirtualTryOnResult: isVTOFromAPI,
    isCanvasIntegrated
  } = usePersonImages();

  // Get VTO result from person image slice
  const personImageState = usePersonImage();
  const {
    displayProps: { personImage: vtoResultImage },
    isVirtualResult: hasVTOResult
  } = personImageState;

  // Get selected VTO result to check useRemovedImage flag
  const selectedVTOResultId = useSelector(state => state.vto?.selectedVTOResultId);
  const { getVTOResultById } = useVTOResults();
  const selectedVTOResult = selectedVTOResultId ? getVTOResultById(selectedVTOResultId) : null;

  // Prioritize VTO result image over backend image
  // Priority: 1) VTO result if active, 2) state.image (could be original after revert), 3) RTK Query activePersonImage with removedBgImage preference
  const hasPersonImage = !!activePersonImage || hasVTOResult || !!vtoResultImage;

  const isVirtualTryOnResult = hasVTOResult || isVTOFromAPI;

  const activeItems = useSelector(selectActiveItems);

  const paidItem = useMemo(() => {
    // Check activeItems first (set by VTO and Lookbook)
    let found = activeItems?.find(item => item.licensingEnabled && item.licensingType === 'paid');
    // Fallback to droppedItems
    if (!found) {
      found = droppedItems?.find(item => item.licensingEnabled && item.licensingType === 'paid');
    }
    return found;
  }, [activeItems, droppedItems]);

  const paidItemSourceId = paidItem?.source_product?.id || paidItem?.source_listing?.id || paidItem?.id;

  const { data: licenseCheck } = useCheckLicenseQuery(
    { listingId: paidItemSourceId, itemType: 'product' },
    { skip: !paidItem }
  );

  const hasLicense = licenseCheck?.hasLicense || false;
  const needsLicense = paidItem && !hasLicense && isVirtualTryOnResult;

  // Determine which image URL to display:
  // 1. If VTO result exists (hasVTOResult=true), use it
  // 2. If activePersonImage exists, respect useRemovedImage flag
  // 3. Fallback to vtoResultImage if nothing else
  const getPersonImageUrl = () => {
    console.log('[VTO Debug] getPersonImageUrl called:', {
      hasVTOResult,
      selectedVTOResultId: selectedVTOResult?.id,
      needsLicense,
      watermarkedImage: selectedVTOResult?.watermarkedImage,
      metadataWatermark: personImageState?.displayProps?.metadata?.watermarkedImage,
      vtoResultImage
    });

    // Priority 1: VTO result with selected VTO result ID
    if (hasVTOResult && selectedVTOResult) {
      if (needsLicense) {
        const storedWatermarkUrl = Array.isArray(selectedVTOResult.watermarkedImage) ? selectedVTOResult.watermarkedImage[0]?.url : selectedVTOResult.watermarkedImage?.url;
        const cleanUrl = (Array.isArray(selectedVTOResult.resultImage) ? selectedVTOResult.resultImage[0]?.url : selectedVTOResult.resultImage?.url) || vtoResultImage;
        // Never fall through to the clean image when a licence is required:
        // derive the watermark if the stored record is missing.
        return resolveLicensedPreviewUrl(storedWatermarkUrl, cleanUrl, selectedVTOResult?.designerName);
      }
      const shouldUseRemoved = selectedVTOResult.useRemovedImage !== false && (Array.isArray(selectedVTOResult.removedBgImage) ? selectedVTOResult.removedBgImage[0]?.url : selectedVTOResult.removedBgImage?.url);
      return shouldUseRemoved ? (Array.isArray(selectedVTOResult.removedBgImage) ? selectedVTOResult.removedBgImage[0]?.url : selectedVTOResult.removedBgImage?.url) : ((Array.isArray(selectedVTOResult.resultImage) ? selectedVTOResult.resultImage[0]?.url : selectedVTOResult.resultImage?.url) || vtoResultImage);
    }

    // Priority 2: VTO result without selected VTO result (e.g., after bg removal)
    if (hasVTOResult && vtoResultImage) {
      if (needsLicense) {
        const storedWatermarkUrl = personImageState?.displayProps?.metadata?.watermarkedImage;
        return resolveLicensedPreviewUrl(storedWatermarkUrl, vtoResultImage, personImageState?.displayProps?.metadata?.designerName);
      }
      return vtoResultImage;
    }

    // Priority 3: Active person image from RTK Query - respect useRemovedImage flag
    if (activePersonImage) {
      const shouldUseRemoved = activePersonImage.useRemovedImage !== false && activePersonImage.removedBgImage?.url;
      return shouldUseRemoved ? activePersonImage.removedBgImage.url : (activePersonImage.image?.url || activePersonImage.image);
    }

    // Priority 4: Fallback to vtoResultImage if it exists (legacy state)
    if (vtoResultImage) {
      return vtoResultImage;
    }

    return null;
  };

  const personImageUrl = getPersonImageUrl();



  return (
    <div
      ref={placeholderRef}
      className="sticky-placeholder"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onPlaceholderClick}
    >
      {/* Canvas Toolbar - Right side */}
      <CanvasToolbar
        onPersonClick={onDirectPersonModalOpen}
        onBackgroundClick={onDirectBackgroundModalOpen}
        onClothingPanelToggle={onClothingPanelToggle}
        onLookbookToggle={onLookbookToggle}
        onCommunityToggle={onCommunityToggle}
        onEarningsToggle={onEarningsToggle}
        isLoggedIn={isLoggedIn}
        onLoginRequired={onLoginRequired}
        isPersonModalOpen={isPersonModalOpen}
        isBackgroundModalOpen={isBackgroundModalOpen}
        isClothingPanelActive={isClothingPanelActive}
        isLookbookActive={isLookbookActive}
        isCommunityActive={isCommunityActive}
        isEarningsActive={isEarningsActive}
        background={background}
      />
      <BackgroundCanvas
        background={background}
        width="100%"
        height="100%"
        className="main-canvas"
        onBackgroundClick={isLoggedIn ? onBackgroundModalOpen : onLoginRequired}
      >
        {/* Person Image Layer - Above background but below clothing items */}
        {hasPersonImage && isCanvasIntegrated && (
          <PersonImageLayer
            key={personImageUrl} // Force re-render when image URL changes
            personImage={personImageUrl}
            position={position}
            scale={scale}
            backgroundRemoved={false} // Background removal is handled by backend
            isVirtualTryOnResult={isVirtualTryOnResult}
            onImageClick={isLoggedIn ? onPersonImageModalOpen : onLoginRequired}
            onBackgroundClick={isLoggedIn ? onBackgroundModalOpen : onLoginRequired}
            canvasWidth={placeholderWidth}
            canvasHeight={placeholderHeight}
            showPreviewBadge={needsLicense}
          />
        )}

        {/* Grid overlay - Above person image but below clothing items */}
        <GridOverlay
          isVisible={showGrid}
          gridSize={gridSize}
        />

        {/* Snap guides - Above grid but below clothing items */}
        <SnapGuides
          isVisible={showSnapGuides && isMoving}
          snapLines={snapLines}
          containerWidth={placeholderWidth}
          containerHeight={placeholderHeight}
        />

        {/* Show empty state when no person image uploaded */}
        {(!hasPersonImage || !isCanvasIntegrated) && droppedItems.length === 0 && !(isPersonImageUploading || isPhotoUploading) && (() => {
          const personSize = placeholderWidth && placeholderWidth < 500 ? 120 : 180;
          return (
          <div
            className="canvas-empty-state-overlay"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.classList.add('drag-over');
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.classList.remove('drag-over');
              // Delegate to parent's drop handler which processes and uploads the file
              onDrop(e);
            }}
          >
            <div
              className="empty-state-content"
              onClick={() => {
                if (!isLoggedIn && onLoginRequired) {
                  onLoginRequired();
                } else if (onPersonImageModalOpen) {
                  onPersonImageModalOpen();
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <PersonPlaceholder size={personSize} />
              <div className="placeholder-subtitle" style={{ fontSize: 13, color: '#aaa', marginTop: 8 }}>Drop or upload a photo</div>
              <PhotoActions
                initialFacingMode="user"
                onLoginRequired={onLoginRequired}
                onFile={file => handleLocalFileUpload({ target: { files: [file] } })}
              />
            </div>
          </div>
          );
        })()}
        {/* Virtual Try-On Loading Indicator */}
        {isTryOnLoading && (
          <TryOnLoadingIndicator
            isLoading={isTryOnLoading}
            message="Processing virtual try-on..."
          />
        )}

        {/* Person Image Upload Loading */}
        {(isPersonImageUploading || isPhotoUploading) && (
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            gap: 12
          }}>
            <div style={{
              width: 36, height: 36,
              border: '3px solid #e0e0e0',
              borderTopColor: '#00b5ad',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ color: '#00b5ad', fontSize: 14, fontWeight: 500 }}>Uploading person image…</span>
          </div>
        )}

        {/* Virtual Try-On Error Handler */}
        {tryOnError && (
          <TryOnErrorHandler
            error={tryOnError}
            onRetry={() => clearTryOnError()}
            onFallback={() => {
              clearTryOnError();
            }}
          />
        )}

        <div className="dropped-items-layer">
          {droppedItems.map((item, index) => (
            <CanvasItem
              key={`${item.id}-${index}`}
              item={item}
              index={index}
              isSelected={multiSelectedItems.length === 1 && multiSelectedItems.includes(item.id)}
              isMultiSelected={multiSelectedItems.length > 1 && multiSelectedItems.includes(item.id)}
              onSelect={onItemClick}
              onMove={(itemIndex, position) => {
                // Handle real-time move updates
                const updatedItems = droppedItems.map((item, i) =>
                  i === itemIndex ? { ...item, ...position } : item
                );
                // You'll need to add this prop to handle real-time updates
                if (onItemMove) {
                  onItemMove(updatedItems);
                }
              }}
              onResize={(itemIndex, dimensions) => {
                // Handle real-time resize updates
                const updatedItems = droppedItems.map((item, i) =>
                  i === itemIndex ? { ...item, ...dimensions } : item
                );
                if (onItemResize) {
                  onItemResize(updatedItems);
                }
              }}
              onRotate={(itemIndex, rotation) => {
                // Handle real-time rotate updates
                const updatedItems = droppedItems.map((item, i) =>
                  i === itemIndex ? { ...item, rotation } : item
                );
                if (onItemRotate) {
                  onItemRotate(updatedItems);
                }
              }}
              onMoveStart={onMoveStart}
              onMoveEnd={(itemIndex) => {
                // Commit move changes
                if (onMoveEnd) {
                  onMoveEnd(itemIndex);
                }
              }}
              onResizeStart={onResizeStart}
              onResizeEnd={(itemIndex) => {
                // Commit resize changes
                if (onResizeEnd) {
                  onResizeEnd(itemIndex);
                }
              }}
              onRotateStart={onRotateStart}
              onRotateEnd={(itemIndex) => {
                // Commit rotate changes
                if (onRotateEnd) {
                  onRotateEnd(itemIndex);
                }
              }}
              onRevertToOriginal={onRevertToOriginal}
              onRetryVirtualTryOn={onRetryVirtualTryOn}
            />
          ))}
        </div>
      </BackgroundCanvas>

      {/* Selection feedback */}
      <SelectionFeedback
        selectedCount={selectedItem !== null ? 1 : 0}
        multiSelectedCount={multiSelectedItems.length}
        totalItems={droppedItems.length}
        lastAction={selectionFeedback}
      />
    </div>
  );
};

export default MyClosetCanvas;
